import moment, { Moment } from 'moment-timezone'
import React from 'react'
import BudgetRecurringActive from 'src/assets/images/Budget-Recurring-Active.svg'
import BudgetRecurringInactive from 'src/assets/images/Budget-Recurring-Inactive.svg'
import BudgetStandardActive from 'src/assets/images/Budget-Standard-Active.svg'
import BudgetStandardInactive from 'src/assets/images/Budget-Standard-Inactive.svg'
import EmptyLabel from 'src/components/EmptyLabel/EmptyLabel'
import { AmexCardHolderTypes, AmexCardTypes, ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { translateNode } from 'src/localization/translateNode'
import { CardImageModel, CardImageResponse } from 'src/models/cardImage.models'
import {
  RecurrenceModel,
  RecurrenceResponse,
} from 'src/models/recurrence.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { IssuerCycleConfig } from 'src/models/virtualCard.models'
import { Address, toAddress } from 'src/queries/addresses/address.models'
import type { MissingCardDetail } from 'src/queries/creditCard.derivations'
import { toDollars } from 'src/utils/currency.utils'
import { convertUTCTimezone } from 'src/utils/date.utils'

export enum CreditCardType {
  Delegate = 'DELEGATE',
  Source = 'SOURCE',
}

export enum CreditCardStatus {
  Active = 'ACTIVE',
  Expired = 'EXPIRED',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
  Blocked = 'BLOCKED',
  Closed = 'CLOSED',
  NotApplicable = 'NOT_APPLICABLE',
}

export enum PhysicalCardStatusReason {
  IssuerNotSupported = 'ISSUER_NOT_SUPPORTED',
  UnknownFailure = 'UNKNOWN_FAILURE',
  CvsVerificationFailed = 'CVS_VERIFICATION_FAILED',
}

export enum PhysicalCardStatus {
  Active = 'ACTIVE',
  Expired = 'EXPIRED',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
  Blocked = 'BLOCKED',
  Closed = 'CLOSED',
  NotApplicable = 'NOT_APPLICABLE',
  NeedsVerification = 'NEEDS_VERIFICATION',
  NeedsCVSVerification = 'NEEDS_CVS_VERIFICATION',
  // No physical card is enrolled yet, but this card is eligible to enable one
  // (e.g. a card registered virtually on Starter, then upgraded). Surfaced so
  // the physical-card transactions panel / enable-physical CTA can show.
  // Response-only, view-derived status (never persisted). See extend-api PR #9823.
  EnrollmentAvailable = 'ENROLLMENT_AVAILABLE',
}

export enum CreditCardDisplayStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
  Closed = 'CLOSED',
}

enum CreditCardNumberFormat {
  CardNumber14 = 'cardnumber14',
  CardNumber15 = 'cardnumber15',
  CardNumber16 = 'cardnumber16',
}

export const BinRangeRegexes = {
  visa: '^4',
  amex: '^(34|37)',
  mastercard: '^5[1-5]',
  discover: '^6011',
  unionpay: '^62',
  troy: '^9792',
  diners: '^(30[0-5]|36)',
}

interface CreditCardFeatureResponse {
  budgets?: boolean
  customAddress?: boolean
  direct?: boolean
  needsCardholderVerification: boolean
  plasticCardsEnabled?: boolean
  rcnReplacementEnabled?: boolean
  recurrence?: boolean
  singleExactPayEnabled?: boolean
  physicalTransactionsEnabled?: boolean
  billPay?: boolean
  balanceLockedEnabled?: boolean
  issuerCycleEnabled?: boolean
}

class CreditCardFeatureModel {
  public budgets?: boolean
  public customAddress?: boolean
  public direct?: boolean
  public needsCardholderVerification: boolean
  public plasticCardsEnabled?: boolean
  public rcnReplacementEnabled?: boolean
  public recurrence?: boolean
  public singleExactPayEnabled?: boolean
  public physicalTransactionsEnabled?: boolean
  public billPay?: boolean
  public balanceLockedEnabled?: boolean
  public issuerCycleEnabled?: boolean

  public constructor(data: CreditCardFeatureResponse) {
    this.budgets = data.budgets
    this.customAddress = data.customAddress
    this.direct = data.direct
    this.needsCardholderVerification = data.needsCardholderVerification
    this.plasticCardsEnabled = data.plasticCardsEnabled
    this.rcnReplacementEnabled = data.rcnReplacementEnabled
    this.recurrence = data.recurrence
    this.singleExactPayEnabled = data.singleExactPayEnabled
    this.physicalTransactionsEnabled = data.physicalTransactionsEnabled
    this.billPay = data.billPay
    this.balanceLockedEnabled = data.balanceLockedEnabled
    this.issuerCycleEnabled = data.issuerCycleEnabled
  }
}

export interface CreditCardStatistics {
  vcnCount: number
  activeVcnCount: number
  budgetCount: number
  activeBudgetCount: number
  clearedTransactionDebitAmount: number
  clearedTransactionCreditAmount: number
}

export interface CardProperties {
  cardType?: AmexCardTypes
  cardHolderType?: AmexCardHolderTypes
  creditCardId?: string
  id?: string
}

type PhysicalCardData = {
  integratorId: string | null
  status: PhysicalCardStatus | null
  statusReason: PhysicalCardStatusReason | null
  feedEnabled: boolean | null
  verificationId: string | null
  missingCardDetails?: MissingCardDetail[]
}

export interface CreditCardListResponse {
  acceptedTermsAt?: string
  activeIssuedAmountCents: number
  activeUntil?: string
  address: Address
  cardImage: CardImageResponse
  companyName: string
  createdAt: string
  currency: ExtendCurrency
  displayName: string
  expires: string
  features: CreditCardFeatureResponse
  id: string
  inactiveSince?: string
  issuedAmountCents: number
  issuer: string
  issuerAccountBalanceCents?: number
  issuerAccountCreditLimitCents?: number
  issuerAvailableLimitCents?: number
  issuerId: string
  issuerName: string
  issuerPhysicalAvailableBalanceCents: number
  issuingLimitCents?: number
  last4: string
  lastVerificationRequestedAt?: string
  numberFormat: CreditCardNumberFormat
  owner: UserListResponse
  ownerId: string
  organizationId?: string
  parentCreditCardId?: string
  parentCreditCardDisplayName?: string
  policyInfoId?: string
  postal: string
  readOnly: boolean
  recurrence?: RecurrenceResponse
  recurs: boolean
  remainingAmountCents: number
  displayStatus: CreditCardDisplayStatus
  status: CreditCardStatus
  timezone: string
  totalActiveSpentCents: number
  totalActiveVirtualCardBalanceCents: number
  type: CreditCardType
  until?: string
  updatedAt: string
  user: UserListResponse
  userId: string
  users?: UserListResponse[]
  cardProperties?: CardProperties
  notes?: string
  partner: {
    status?: CreditCardStatus
    virtualCardsSupported?: boolean
    isVirtualAccount?: boolean
  }
  physicalClearingsEnabled?: boolean
  physicalCard: PhysicalCardData
  issuerCycleConfig?: IssuerCycleConfig | null
}

export class CreditCardListModel {
  public acceptedTermsAt?: string
  public activeIssuedAmountCents: number
  public activeUntil?: string
  public address: Address
  /**
   * The single budget user, hydrated from the `user` the list response sends
   * alongside `userId`. Same rationale as {@link owner}.
   *
   * A budget with SEVERAL users reports them through `budgetUsers`; this is
   * still populated in that case (the wire sends both), so read `budgetUsers`
   * first when the distinction matters.
   */
  public budgetUser?: UserListModel
  public budgetUsers?: UserListModel[]
  public cardImage: CardImageModel
  public cardProperties?: CardProperties
  public companyName: string
  public createdAt: string
  public currency: ExtendCurrency
  public displayName: string
  public displayStatus: CreditCardDisplayStatus
  public expires: string
  public features: CreditCardFeatureModel
  public id: string
  public inactiveSince?: string
  public issuedAmountCents: number
  public issuer: string
  public issuerAccountBalanceCents?: number
  public issuerAccountCreditLimitCents?: number
  public issuerAvailableLimitCents?: number
  public issuerId: string
  public issuerName: string
  public issuerPhysicalAvailableBalanceCents: number
  public issuingLimitCents?: number
  public last4: string
  public lastVerificationRequestedAt?: string
  public notes?: string
  public numberFormat: CreditCardNumberFormat
  public organizationId?: string
  /**
   * The account owner, hydrated from the `owner` the list response already
   * sends alongside `ownerId`.
   *
   * Built here for the same reason — and in the same shape — as `budgetUsers`
   * just above: consumers need a `UserListModel`, and constructing one per
   * render per row is both wasted work and a fresh identity every time.
   *
   * Optional even though the wire field is not, because several fixtures and
   * legacy normalizers hand this constructor a partial response.
   */
  public owner?: UserListModel
  public ownerId: string
  public parentCreditCardDisplayName?: string
  public parentCreditCardId?: string
  public partner: {
    status?: CreditCardStatus
    virtualCardsSupported?: boolean
    isVirtualAccount?: boolean
  }
  public physicalCard: PhysicalCardData
  public physicalClearingsEnabled?: boolean
  public issuerCycleConfig?: IssuerCycleConfig | null
  public policyInfoId?: string
  public postal: string
  public readOnly: boolean
  public recurrence?: RecurrenceModel
  public recurs: boolean
  public remainingAmountCents?: number
  public status: CreditCardStatus
  public timezone: string
  public totalActiveSpentCents: number
  public totalActiveVirtualCardBalanceCents: number
  public type: CreditCardType
  public until?: string
  public updatedAt: string
  public user: UserListResponse
  public userId: string

  public constructor(data: CreditCardListResponse) {
    this.acceptedTermsAt = data.acceptedTermsAt
    this.activeIssuedAmountCents = data.activeIssuedAmountCents
    this.activeUntil = data.activeUntil
    this.address = toAddress(data.address)
    // `!= null`, not `!== undefined`: the wire is free to send an explicit
    // null for a user that has been removed, and `new UserListModel(null)`
    // throws reading `data.id`. This constructor runs for every credit card the
    // app loads — list, detail and the create/update write responses — so a
    // single null would take down surfaces that never read this field.
    this.budgetUser =
      data.user != null ? new UserListModel(data.user) : undefined
    this.budgetUsers =
      data.users != null
        ? data.users.map((user) => new UserListModel(user))
        : undefined
    this.cardImage = new CardImageModel(data.cardImage)
    this.cardProperties = data.cardProperties
    this.companyName = data.companyName
    this.createdAt = data.createdAt
    this.currency = data.currency
    this.displayName = data.displayName
    this.displayStatus = data.displayStatus
    this.expires = data.expires
    this.features = new CreditCardFeatureModel(data.features)
    this.id = data.id
    this.inactiveSince = data.inactiveSince
    this.issuedAmountCents = data.issuedAmountCents
    this.issuer = data.issuer
    this.issuerAccountBalanceCents = data.issuerAccountBalanceCents
    this.issuerAccountCreditLimitCents = data.issuerAccountCreditLimitCents
    this.issuerAvailableLimitCents = data.issuerAvailableLimitCents
    this.issuerId = data.issuerId
    this.issuerName = data.issuerName
    this.issuerPhysicalAvailableBalanceCents =
      data.issuerPhysicalAvailableBalanceCents
    this.issuingLimitCents = data.issuingLimitCents
    this.last4 = data.last4
    this.lastVerificationRequestedAt = data.lastVerificationRequestedAt
    this.notes = data.notes
    this.numberFormat = data.numberFormat
    this.organizationId = data.organizationId
    // `!= null` for the same reason as `budgetUser` above.
    this.owner = data.owner != null ? new UserListModel(data.owner) : undefined
    this.ownerId = data.ownerId
    this.parentCreditCardDisplayName = data.parentCreditCardDisplayName
    this.parentCreditCardId = data.parentCreditCardId
    this.partner = data.partner
      ? {
          status: data.partner?.status,
          virtualCardsSupported: data.partner.virtualCardsSupported,
          isVirtualAccount: data.partner.isVirtualAccount,
        }
      : {
          status: undefined,
          virtualCardsSupported: undefined,
          isVirtualAccount: undefined,
        }
    this.physicalCard = { ...data.physicalCard }
    this.physicalClearingsEnabled = data.physicalClearingsEnabled
    this.policyInfoId = data.policyInfoId
    this.postal = data.postal
    this.readOnly = data.readOnly
    this.recurrence =
      data.recurrence !== undefined
        ? new RecurrenceModel(data.recurrence)
        : undefined
    this.recurs = data.recurs
    this.remainingAmountCents = data.remainingAmountCents
    this.status = data.status
    this.timezone = data.timezone
    this.totalActiveSpentCents = data.totalActiveSpentCents
    this.totalActiveVirtualCardBalanceCents =
      data.totalActiveVirtualCardBalanceCents
    this.type = data.type
    this.until = data.until
    this.updatedAt = data.updatedAt
    this.user = data.user
    this.userId = data.userId
    this.issuerCycleConfig = data.issuerCycleConfig
  }

  public get issuingLimit(): number | undefined {
    return this.issuingLimitCents !== undefined
      ? toDollars(this.issuingLimitCents)
      : undefined
  }

  public get issuingLimitLabel(): React.ReactElement | undefined {
    return this.issuingLimit !== undefined ? (
      <Crncy currency={this.currency} amount={this.issuingLimit} />
    ) : undefined
  }

  public get totalActiveSpent(): number {
    return toDollars(this.totalActiveSpentCents)
  }

  public get totalActiveSpentLabel(): React.ReactElement {
    return this.totalActiveSpentCents === 0 ? (
      <EmptyLabel />
    ) : (
      <Crncy currency={this.currency} amount={this.totalActiveSpent} />
    )
  }

  public get issuedAmount(): number {
    return toDollars(this.issuedAmountCents)
  }

  public get issuedAmountLabel(): React.ReactElement {
    return this.issuedAmountCents === 0 ? (
      <EmptyLabel />
    ) : (
      <Crncy currency={this.currency} amount={this.issuedAmount} />
    )
  }

  public get activeIssuedAmount(): number {
    return toDollars(this.activeIssuedAmountCents)
  }

  public get activeIssuedAmountLabel(): React.ReactElement {
    return this.activeIssuedAmountCents === 0 ? (
      <EmptyLabel />
    ) : (
      <Crncy currency={this.currency} amount={this.activeIssuedAmount} />
    )
  }

  public get issuerPhysicalAvailableBalance(): number {
    return toDollars(this.issuerPhysicalAvailableBalanceCents)
  }

  public get issuerPhysicalAvailableBalanceCentsLabel(): React.ReactElement {
    return this.issuerPhysicalAvailableBalanceCents === 0 ? (
      <EmptyLabel />
    ) : (
      <Crncy
        currency={this.currency}
        amount={this.issuerPhysicalAvailableBalance}
      />
    )
  }

  public get issuerAccountBalance(): number | undefined {
    return this.issuerAccountBalanceCents !== undefined
      ? toDollars(this.issuerAccountBalanceCents)
      : undefined
  }

  public get issuerAccountBalanceLabel(): React.ReactElement | undefined {
    return this.issuerAccountBalance !== undefined ? (
      <Crncy currency={this.currency} amount={this.issuerAccountBalance} />
    ) : undefined
  }

  public get unissuedAmountCents(): number | undefined {
    return this.remainingAmountCents !== undefined
      ? Math.max(this.remainingAmountCents, 0)
      : undefined
  }

  public get unissuedAmount(): number | undefined {
    return this.unissuedAmountCents !== undefined
      ? toDollars(this.unissuedAmountCents)
      : undefined
  }

  public get unissuedAmountLabel(): React.ReactElement | undefined {
    return this.isBudget ? (
      this.isInactive || this.isClosed ? (
        translateNode('Inactive')
      ) : this.unissuedAmount !== undefined ? (
        <Crncy currency={this.currency} amount={this.unissuedAmount} />
      ) : (
        translateNode('Unlimited')
      )
    ) : undefined
  }

  public get unspentAmountCents(): number {
    return this.totalActiveVirtualCardBalanceCents
  }

  public get unspentAmount(): number {
    return toDollars(this.unspentAmountCents)
  }

  public get unspentAmountLabel(): React.ReactElement {
    return this.unspentAmountCents === 0 ? (
      <EmptyLabel />
    ) : (
      <Crncy currency={this.currency} amount={this.unspentAmount} />
    )
  }

  public get isActive(): boolean {
    return this.displayStatus === CreditCardDisplayStatus.Active
  }

  public get isPending(): boolean {
    return this.displayStatus === CreditCardDisplayStatus.Pending
  }

  // TODO: status and physicalCard.status can be out of sync (e.g. status=ACTIVE
  // while physicalCard.status=BLOCKED). Decide whether isBlocked should affect
  // card-level behavior (disabling actions, hiding features) or only display.
  public get isBlocked(): boolean {
    return (
      this.status === CreditCardStatus.Blocked ||
      this.physicalCard.status === PhysicalCardStatus.Blocked
    )
  }

  public get isInactive(): boolean {
    return this.displayStatus === CreditCardDisplayStatus.Inactive
  }

  public get isClosed(): boolean {
    return this.displayStatus === CreditCardDisplayStatus.Closed
  }

  public get isSource(): boolean {
    return this.type === CreditCardType.Source
  }

  public get isBudget(): boolean {
    return this.type === CreditCardType.Delegate
  }

  public get isLimitedBudget(): boolean {
    return this.isBudget && this.issuingLimitCents !== undefined
  }

  public get cardTypeImageSrc(): string {
    if (this.recurs) {
      return this.isActive ? BudgetRecurringActive : BudgetRecurringInactive
    } else {
      return this.isActive ? BudgetStandardActive : BudgetStandardInactive
    }
  }

  public get truncatedDisplayName(): string {
    return this.displayName.length > 45
      ? `${this.displayName.slice(0, 44)}...`
      : this.displayName
  }

  public get createdAtTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.createdAt)
  }

  public get createdAtTimezonedString(): string {
    return this.createdAtTimezonedMoment.format('ll')
  }

  public get untilTimezonedMoment(): Moment | undefined {
    return this.until !== undefined
      ? convertUTCTimezone(this.timezone, this.until).subtract(1, 'millisecond')
      : undefined
  }

  public get untilTimezonedString(): string | undefined {
    return this.untilTimezonedMoment?.format('ll')
  }

  public get activeUntilTimezonedMoment(): Moment | undefined {
    return this.activeUntil !== undefined
      ? convertUTCTimezone(this.timezone, this.activeUntil).subtract(
          1,
          'millisecond',
        )
      : undefined
  }

  public get activeUntilTimezonedString(): string | undefined {
    return this.activeUntilTimezonedMoment?.format('ll')
  }

  public get updatedAtTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.updatedAt)
  }

  public get updatedAtTimezonedString(): string {
    return this.updatedAtTimezonedMoment.format('l')
  }

  // Expiration dates are calendar month/year values, not timestamps.
  // Applying timezone conversion shifts midnight UTC dates into the previous
  // month for negative UTC offsets (e.g. US Eastern), causing an off-by-one.
  public get expiresUtcMoment(): Moment {
    return moment.utc(this.expires)
  }

  public get expiresFormattedString(): string {
    return this.expiresUtcMoment.format('MM/YY')
  }

  public get inactiveSinceTimezonedMoment(): Moment | undefined {
    return this.inactiveSince !== undefined
      ? convertUTCTimezone(this.timezone, this.inactiveSince)
      : undefined
  }

  public get inactiveSinceTimezonedString(): string | undefined {
    return this.inactiveSinceTimezonedMoment?.format('ll')
  }

  public get updateFormTopText(): React.ReactElement | undefined {
    return translateNode(
      'Any changes you make below will go into effect as soon as you press "Update Budget".',
    )
  }

  public get timeSinceVerified(): number | undefined {
    return this.lastVerificationRequestedAt !== undefined
      ? moment().diff(moment(this.lastVerificationRequestedAt))
      : undefined
  }

  public get timeUntilNextVerificationRequested(): Moment {
    return moment.utc(
      this.timeSinceVerified !== undefined
        ? 60 * 60 * 24 * 1000 - this.timeSinceVerified
        : 0,
    )
  }

  public get canVerifyAgain(): boolean {
    return this.timeSinceVerified !== undefined
      ? this.timeSinceVerified > 60 * 60 * 24 * 1000
      : true
  }
}
